import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputProvider, groupInput, input } from "@redotech/ui/form";
import { memo, useContext } from "react";
import { AuthContext } from "../../../app/auth";
import {
  OutboundLabelForm,
  usersWhoCanEditRateTables,
} from "../outbound-labels";

export const merchantCarrierFeeSettingsForm = groupInput({
  merchantCarrierAccountLabelFee: groupInput({ amount: input<number>() }),
  monthlySubscriptionFee: groupInput({
    amount: input<number>(),
    turnedOnAt: input<Date | undefined>(),
  }),
  wmsSubscriptionFee: groupInput({
    amount: input<number>(),
    turnedOnAt: input<Date | undefined>(),
  }),
});

export type MerchantCarrierFeeSettingsForm = InputProvider.Form<
  typeof merchantCarrierFeeSettingsForm
>;

export type MerchantCarrierFeeSettingsValue = InputProvider.Value<
  typeof merchantCarrierFeeSettingsForm
>;

export const merchantCarrierFeeSettingsDefault: MerchantCarrierFeeSettingsValue =
  {
    merchantCarrierAccountLabelFee: { amount: 0 },
    monthlySubscriptionFee: { amount: 0, turnedOnAt: undefined },
    wmsSubscriptionFee: { amount: 0, turnedOnAt: undefined },
  };

export const MerchantCarrierFeeSettingsCard = memo(
  function MerchantCarrierFeeSettingsCard({
    input,
  }: {
    input: OutboundLabelForm;
  }) {
    const auth = useContext(AuthContext);
    const canEditFees = auth && usersWhoCanEditRateTables.includes(auth.email);

    const { fees } = input.inputs;
    const subscriptionFee = fees.inputs.monthlySubscriptionFee.inputs;
    const perLabelFee =
      fees.inputs.merchantCarrierAccountLabelFee.inputs.amount;
    const wmsSubscriptionFee = fees.inputs.wmsSubscriptionFee.inputs;

    return (
      <Card title="Monthly recurring fees">
        <Flex dir="column" gap="xl">
          <RedoIncrementDecrement
            disabled={!canEditFees}
            label="Merchant Carrier Account Label Fee"
            min={0}
            placeholder="0"
            prefix="$"
            setValue={perLabelFee.setValue}
            step={0.01}
            value={perLabelFee.value}
          />
          <Flex dir="row" gap="2xl">
            <Flex dir="column" gap="2xl">
              {!canEditFees && (
                <Text fontSize="xs" textColor="tertiary">
                  Only approved users can change billing settings
                </Text>
              )}
              <RedoToggle
                disabled={!canEditFees}
                label="Enable OMS monthly subscription fee"
                setValue={(value) => {
                  subscriptionFee.turnedOnAt.setValue(
                    value ? new Date() : undefined,
                  );
                  if (!value) {
                    subscriptionFee.amount.setValue(0);
                  }
                }}
                size="sm"
                value={!!subscriptionFee.turnedOnAt.value}
              />
              {!!subscriptionFee.turnedOnAt.value && (
                <RedoIncrementDecrement
                  disabled={!canEditFees}
                  label="Monthly Subscription Fee"
                  min={0}
                  placeholder="0"
                  prefix="$"
                  setValue={(value) => {
                    subscriptionFee.amount.setValue(value);
                  }}
                  step={1}
                  value={subscriptionFee.amount.value}
                />
              )}
            </Flex>
            <Flex dir="column" gap="2xl">
              <RedoToggle
                disabled={!canEditFees}
                label="Enable WMS monthly subscription fee"
                setValue={(value) => {
                  wmsSubscriptionFee.turnedOnAt.setValue(
                    value ? new Date() : undefined,
                  );
                  if (!value) {
                    wmsSubscriptionFee.amount.setValue(0);
                  }
                }}
                size="sm"
                value={!!wmsSubscriptionFee.turnedOnAt.value}
              />
              {!!wmsSubscriptionFee.turnedOnAt.value && (
                <RedoIncrementDecrement
                  disabled={!canEditFees}
                  label="WMS Monthly Subscription Fee"
                  min={0}
                  placeholder="0"
                  prefix="$"
                  setValue={(value) => {
                    wmsSubscriptionFee.amount.setValue(value);
                  }}
                  step={1}
                  value={wmsSubscriptionFee.amount.value}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>
    );
  },
);
